import { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Trash2, Plus } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { showToast } from '@/services/toast.service';
import TenantConfigService from '@/services/tenantConfig.service';

interface AllowedOriginsCardProps {
  tenantConfigId: string;
  initialOrigins: string[];
}

export default function AllowedOriginsCard({
  tenantConfigId,
  initialOrigins,
}: AllowedOriginsCardProps) {
  const queryClient = useQueryClient();
  const [origins, setOrigins] = useState<string[]>(initialOrigins || []);
  const [pending, setPending] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setOrigins(initialOrigins || []);
  }, [initialOrigins]);

  const save = useCallback(
    async (next: string[], successMsg: string) => {
      setSaving(true);
      try {
        const result = await TenantConfigService.getInstance().saveEmbedFrameAncestors(
          tenantConfigId,
          next,
        );
        setOrigins(result.embed_frame_ancestors || []);
        setPending('');
        showToast.success(successMsg);
        queryClient.invalidateQueries({ queryKey: ['tenantRegistration'] });
      } catch (err: any) {
        // Backend surfaces 409 for already-registered-to-another-tenant and
        // 422 for bad format. The `description` field carries the detail.
        const description = err?.body?.description || err?.message || 'An error occurred';
        showToast.error('Failed to save allowed origins', { description });
      } finally {
        setSaving(false);
      }
    },
    [tenantConfigId, queryClient],
  );

  const addOrigin = useCallback(() => {
    const raw = pending.trim();
    if (!raw) return;
    // Normalize client-side for better UX; server re-normalizes + validates.
    const withScheme = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
    try {
      const u = new URL(withScheme);
      const normalized = `${u.protocol}//${u.host.toLowerCase()}`;
      if (origins.includes(normalized)) {
        showToast.info('That origin is already in the list.');
        return;
      }
      void save([...origins, normalized], `Added ${normalized}`);
    } catch {
      showToast.error('Invalid origin', {
        description: 'Expected something like https://app.customer.com',
      });
    }
  }, [pending, origins, save]);

  const removeOrigin = useCallback(
    (origin: string) => {
      const next = origins.filter((o) => o !== origin);
      void save(next, `Removed ${origin}`);
    },
    [origins, save],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Allowed Origins</CardTitle>
        <CardDescription>
          Sites allowed to embed Pliable via iframe. Only origins listed here can load
          your pages in a <code className="text-xs">&lt;iframe&gt;</code> (enforced by
          CSP <code className="text-xs">frame-ancestors</code>). Origins must not be
          registered to another Pliable account.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {origins.length === 0 ? (
          <p className="text-sm text-muted-foreground">No origins registered yet.</p>
        ) : (
          <ul className="space-y-2">
            {origins.map((origin) => (
              <li
                key={origin}
                className="flex items-center justify-between rounded-md border px-3 py-2"
              >
                <code className="text-sm font-mono">{origin}</code>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removeOrigin(origin)}
                  disabled={saving}
                  aria-label={`Remove ${origin}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}

        <div className="space-y-2">
          <Label htmlFor="new-origin">Add origin</Label>
          <div className="flex gap-2">
            <Input
              id="new-origin"
              placeholder="https://app.customer.com"
              value={pending}
              onChange={(e) => setPending(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addOrigin();
                }
              }}
              disabled={saving}
            />
            <Button onClick={addOrigin} disabled={saving || !pending.trim()}>
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-1" />
                  Add
                </>
              )}
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">
            Include the protocol. Paths and trailing slashes are ignored — origin only
            (e.g. <code className="text-xs">https://app.customer.com</code>).
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
